<template>
  <button
    :class="classes"
    :disabled="disabled || loading"
    :type="type"
    :title="title"
    :aria-label="ariaLabel"
    :tabindex="tabindex"
    @click="emit('click', $event)"
    @keydown="emit('keydown', $event)"
  >
    <UiIcon v-if="loading" name="refresh-cw" class="animate-spin" />
    <slot />
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import UiIcon from './UiIcon.vue'

type Variant =
  | 'primary'
  | 'secondary'
  | 'text'
  | 'error'
  | 'warning'
  | 'success'
  | 'info'
type Size = 'small' | 'default' | 'large' | 'fab'

const props = withDefaults(
  defineProps<{
    variant?: Variant
    size?: Size
    disabled?: boolean
    type?: 'button' | 'submit' | 'reset'
    loading?: boolean
    block?: boolean
    title?: string
    rounded?: boolean
    tabindex?: number
    ariaLabel?: string
  }>(),
  {
    variant: 'primary',
    size: 'default',
    disabled: false,
    type: 'button',
    block: false,
    loading: false,
    rounded: false,
  },
)

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void
  (e: 'keydown', event: KeyboardEvent): void
}>()

// Per-variant Tailwind classes. Hover uses opacity modifiers as an
// approximation of the former color-mix darkening (e.g. bg-coldGray/90).
const variantClasses: Record<Variant, string> = {
  primary: 'bg-coldGray text-white enabled:hover:bg-coldGray-dark',
  secondary: 'bg-secondary text-primary enabled:hover:bg-secondary-dark',
  text: 'bg-transparent text-primary enabled:hover:bg-primary-dark/40',
  error: 'bg-error text-white enabled:hover:bg-error-dark',
  warning: 'bg-warning text-white enabled:hover:bg-warning-dark',
  success: 'bg-success text-white enabled:hover:bg-success-dark',
  info: 'bg-info text-white enabled:hover:bg-info-dark',
}

const sizeClasses: Record<Size, string> = {
  small: 'px-2 py-1 text-sm',
  default: 'px-4 py-2 text-sm',
  large: 'px-6 py-3 text-base',
  fab: 'size-14 text-base rounded-full! shadow-xl',
}

const classes = computed(() =>
  [
    'inline-flex items-center justify-center gap-1 border-0 font-medium transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
    'disabled:cursor-not-allowed disabled:opacity-50 uppercase! whitespace-nowrap',
    variantClasses[props.variant],
    sizeClasses[props.size],
    props.block ? 'w-full' : '',
    props.rounded ? 'rounded-full' : 'rounded',
  ].join(' '),
)
</script>
